import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { User, Mail, Phone, CreditCard, MapPin, Calendar, Save, Edit, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { formatPhoneBR, normalizePhone } from '../../lib/phone';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const PatientProfile = () => {
  const [patient, setPatient] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', cpf: '', rg: '', address: '', birth_date: '' });
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patient/my-info`, { withCredentials: true });
      setPatient(data);
      if (data) {
        setForm({
          name: data.name || '',
          phone: formatPhoneBR(data.phone || ''),
          email: data.email || '',
          cpf: data.cpf || '',
          rg: data.rg || '',
          address: data.address || '',
          birth_date: data.birth_date || ''
        });
      }
    } catch (error) { console.error(error); }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const update = {};
      Object.keys(form).forEach(k => { if (form[k] !== undefined) update[k] = form[k]; });
      // Allow explicit empty email to clear
      if (update.phone) update.phone = normalizePhone(update.phone);
      if (update.email !== undefined) update.email = String(update.email).trim().toLowerCase();
      await axios.put(`${API_URL}/api/patient/my-info`, update, { withCredentials: true });
      toast.success('Cadastro atualizado!');
      setEditing(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error('As senhas não coincidem'); return;
    }
    if ((pwForm.new_password || '').length < 4) {
      toast.error('Nova senha muito curta (mínimo 4 caracteres)'); return;
    }
    setPwSaving(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      }, { withCredentials: true });
      toast.success('Senha alterada com sucesso!');
      setPwOpen(false);
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar senha');
    }
    setPwSaving(false);
  };

  if (!patient) {
    return (
      <div className="space-y-6" data-testid="patient-profile">
        <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Meu Cadastro</h2>
        <Card className="border-[#E5DCC8]">
          <CardContent className="p-8 text-center">
            <User className="h-12 w-12 text-[#7A85A0] mx-auto mb-3" />
            <p className="text-[#7A85A0]">Cadastro não encontrado.</p>
            <p className="text-sm text-[#7A85A0] mt-1">Entre em contato com a clínica.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const InfoRow = ({ icon, label, value }) => (
    <div className="flex items-center gap-3 py-3 border-b border-[#FAF6EE] last:border-0">
      <div className="p-2 bg-[#0F1B3D]/5 rounded-lg">
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-xs text-[#7A85A0]">{label}</p>
        <p className="text-sm font-medium text-[#0F1B3D]">{value || '—'}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="patient-profile">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Meu Cadastro</h2>
          <p className="text-[#5F6B85]">Seus dados pessoais</p>
        </div>
        {!editing && (
          <Button variant="outline" className="border-[#E5DCC8]" onClick={() => setEditing(true)} data-testid="edit-profile-button">
            <Edit className="h-4 w-4 mr-2" /> Editar
          </Button>
        )}
      </div>

      {!editing ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-[#E5DCC8]">
            <CardHeader>
              <CardTitle className="text-lg">Dados Pessoais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-0">
              <InfoRow icon={<User className="h-4 w-4 text-[#0F1B3D]" />} label="Nome Completo" value={patient.name} />
              <InfoRow icon={<Mail className="h-4 w-4 text-[#0F1B3D]" />} label="Email" value={patient.email} />
              <InfoRow icon={<Phone className="h-4 w-4 text-[#0F1B3D]" />} label="Celular" value={formatPhoneBR(patient.phone || '')} />
              <InfoRow icon={<CreditCard className="h-4 w-4 text-[#0F1B3D]" />} label="CPF" value={patient.cpf} />
              <InfoRow icon={<CreditCard className="h-4 w-4 text-[#0F1B3D]" />} label="RG" value={patient.rg} />
            </CardContent>
          </Card>

          <Card className="border-[#E5DCC8]">
            <CardHeader>
              <CardTitle className="text-lg">Informações Adicionais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-0">
              <InfoRow icon={<Calendar className="h-4 w-4 text-[#0F1B3D]" />} label="Data de Nascimento" value={patient.birth_date ? new Date(patient.birth_date + 'T12:00').toLocaleDateString('pt-BR') : '—'} />
              <InfoRow icon={<MapPin className="h-4 w-4 text-[#0F1B3D]" />} label="Endereço" value={patient.address} />
              <InfoRow icon={<Calendar className="h-4 w-4 text-[#0F1B3D]" />} label="Cadastrado em" value={patient.created_at ? new Date(patient.created_at).toLocaleDateString('pt-BR') : '—'} />
              <InfoRow icon={<User className="h-4 w-4 text-[#0F1B3D]" />} label="Status" value={patient.status === 'active' ? 'Ativo' : patient.status === 'inactive' ? 'Inativo' : patient.status} />
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card className="border-[#E5DCC8]">
          <CardHeader>
            <CardTitle className="text-lg">Editar Cadastro</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome Completo</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Celular (WhatsApp)</Label>
                  <Input value={form.phone} onChange={e => setForm({ ...form, phone: formatPhoneBR(e.target.value) })} inputMode="numeric" maxLength={15} placeholder="(00) 00000-0000" data-testid="profile-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label>E-mail</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="seu@email.com" data-testid="profile-email-input" />
                </div>
                <div className="space-y-2">
                  <Label>CPF</Label>
                  <Input value={form.cpf} onChange={e => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" data-testid="profile-cpf-input" />
                </div>
                <div className="space-y-2">
                  <Label>RG</Label>
                  <Input value={form.rg} onChange={e => setForm({ ...form, rg: e.target.value })} data-testid="profile-rg-input" />
                </div>
                <div className="space-y-2">
                  <Label>Data de Nascimento</Label>
                  <Input type="date" value={form.birth_date} onChange={e => setForm({ ...form, birth_date: e.target.value })} data-testid="profile-birthdate-input" />
                </div>
                <div className="space-y-2">
                  <Label>Endereço</Label>
                  <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="profile-address-input" />
                </div>
              </div>
              <div className="flex gap-3 flex-wrap">
                <Button type="submit" className="bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="save-profile-button">
                  <Save className="h-4 w-4 mr-2" /> Salvar
                </Button>
                <Button type="button" variant="outline" className="border-[#E5DCC8]" onClick={() => setEditing(false)}>
                  Cancelar
                </Button>
                <Button type="button" variant="outline" className="border-[#E5DCC8] ml-auto" onClick={() => setPwOpen(true)} data-testid="open-change-password">
                  <KeyRound className="h-4 w-4 mr-2" /> Alterar senha
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {pwOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setPwOpen(false)} data-testid="change-password-modal">
          <div className="bg-white rounded-2xl border border-[#E5DCC8] shadow-2xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <KeyRound className="h-5 w-5 text-[#A8174E]" />
              <h3 className="text-lg font-semibold text-[#0F1B3D]">Alterar senha</h3>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-3">
              <div className="space-y-2">
                <Label>Senha atual</Label>
                <Input type="password" value={pwForm.current_password} onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })} required data-testid="pw-current" />
              </div>
              <div className="space-y-2">
                <Label>Nova senha</Label>
                <Input type="password" value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })} required minLength={4} data-testid="pw-new" />
              </div>
              <div className="space-y-2">
                <Label>Confirmar nova senha</Label>
                <Input type="password" value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} required minLength={4} data-testid="pw-confirm" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="submit" className="bg-[#A8174E] hover:bg-[#8E1142] text-white flex-1" disabled={pwSaving} data-testid="pw-submit">
                  {pwSaving ? 'Salvando...' : 'Alterar senha'}
                </Button>
                <Button type="button" variant="outline" className="border-[#E5DCC8]" onClick={() => setPwOpen(false)}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
